import { useMutation, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { queryClient } from "@/main.tsx";
import {
  createTab,
  deleteTab,
  diffTabSpec,
  listTabs,
  publishTabSpec,
  pullTabSpec,
  updateTab,
  type ISpecDiff,
} from "../services/tab-service.ts";
import type { ICreateTab, ITab, IUpdateTab } from "../types/tab.types.ts";

const tabsKey = (spaceId: string) => ["tabs", spaceId] as const;

export function useTabsQuery(spaceId?: string): UseQueryResult<ITab[], Error> {
  return useQuery({
    queryKey: tabsKey(spaceId ?? ""),
    queryFn: () => listTabs(spaceId as string),
    enabled: !!spaceId,
    staleTime: 30 * 1000,
  });
}

export function useCreateTabMutation() {
  return useMutation<ITab, Error, ICreateTab>({
    mutationFn: createTab,
    onSuccess: (tab) => {
      queryClient.invalidateQueries({ queryKey: tabsKey(tab.spaceId) });
    },
  });
}

export function useUpdateTabMutation(spaceId: string) {
  return useMutation<ITab, Error, IUpdateTab>({
    mutationFn: updateTab,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tabsKey(spaceId) });
    },
  });
}

export function useDeleteTabMutation(spaceId: string) {
  return useMutation<void, Error, string>({
    mutationFn: deleteTab,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tabsKey(spaceId) });
      // Pages may have lost their tabId — refresh sidebar trees.
      queryClient.invalidateQueries({ queryKey: ["root-sidebar-pages"] });
      queryClient.invalidateQueries({ queryKey: ["sidebar-pages"] });
    },
  });
}

export function usePullTabSpecMutation(spaceId: string) {
  return useMutation<ITab, Error, string>({
    mutationFn: pullTabSpec,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tabsKey(spaceId) });
    },
  });
}

export function usePublishTabSpecMutation(spaceId: string) {
  return useMutation<ITab, Error, string>({
    mutationFn: publishTabSpec,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tabsKey(spaceId) });
    },
  });
}

/** Lazy diff query — only enabled while the editor stares at the dialog. */
export function useTabSpecDiffQuery(
  tabId: string | undefined,
  enabled: boolean,
): UseQueryResult<ISpecDiff, Error> {
  return useQuery({
    queryKey: ["tab-spec-diff", tabId],
    queryFn: () => diffTabSpec(tabId as string),
    enabled: !!tabId && enabled,
    staleTime: 0,
  });
}
